import moment from 'moment';
import _ from 'underscore';
import Vue from 'vue';
import dumper from 'vue-dumper';

import { decycle } from './cycle';

const emailRegex = /^(([^<>()[\]\\.,;:@"]+(\.[^<>()[\]\\.,;:@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const statusToFlagOption = {};

// Convert the given input to a UTC date representing the same Year, Month and Day.
function createUTCDate(input) {
  // Since we primarily care about the date which is entered and not the full
  // timestamp, we must take care to create a date in UTC. In some cases, i.e.,
  // when changing timezones would result in the day shifting to the previous or
  // next one, we would store an incorrect date once that automatic timezone
  // shifting happened.
  //
  // For ex:
  // moment("2019-09-30 23:58:00 -04:00").toJSON() // "2019-10-01T03:58:00.000Z"
  // moment("2019-09-30 03:58:00 +05:30").toJSON() // "2019-09-29T22:28:00.000Z"
  const d = moment(input).toObject();
  const m = moment.utc().set({
    years: d.years,
    months: d.months,
    date: d.date,
    hours: '12',
    minutes: '00',
    seconds: '00',
    milliseconds: '000',
  });
  return m;
}

// moment.fn.toJSON = function toJSON() {
//   // Override the default toJSON to always convert to UTC before formatting.
//   //
//   if (this._isUTC) {
//     return this.toISOString();
//   }
//   return createUTCDate(this).toISOString();
// };

/**
 * Returns true if the object is null, undefined, or any array-like Object with
 * zero length (e.g., empty Array or String).
 *
 * @param {Object} obj to test
 */
export function isBlank(obj) {
  return obj === null || obj === undefined || (_.has(obj, 'length') && obj.length === 0);
}

export function ready(fn) {
  if (document.readyState !== 'loading') {
    fn();
    return;
  }
  document.addEventListener('DOMContentLoaded', fn, false);
}

//
//
/**
 * inert returns a non-reactive version (copy) of the given object. In effect,
 * it will be a deep-clone of the given object.
 *
 * i.e., it strips all of the reactive properties and returns just a plain
 * object with just the data such that it can be manipulated without
 * triggering vuex state warnings.
 *
 * @param {Object} obj Object to make inert
 * @param {Boolean} resetIDs when true, resets UUID & id fields for all cloned models
 */
export function inert(obj, resetIDs) {
  if (Array.isArray(obj)) {
    return obj.map((e) => inert(e, resetIDs));
  }

  if (obj === undefined || obj === null) {
    return obj;
  }

  // make a single object inert
  return obj.inertClone ? obj.inertClone(resetIDs) : JSON.parse(JSON.stringify(obj));
}

/**
 * Helper for running bits of code in non-prod environments.
 *
 * Set at system start with something like:
 *
 * common.isDev = (process.env.NODE_ENV !== 'production' || window.location.hostname !== config.PROD_HOSTNAME);
 */
// eslint-disable-next-line prefer-const
let isDev = false;

/**
 * Wrapper around console.warn which only logs in non-prod envs
 *
 * @param  {...any} obj Args to log as warning
 */
export function warn(...obj) {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.warn(...obj);
  }
}

/**
 * Wrapper around console.debug which only logs in non-prod envs
 * @param  {...any} obj Args to log
 */
export function debug(...obj) {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.debug(...obj);
  }
}

/**
 * Log the given error and report to Sentry, if enabled.
 * @param {Error} err
 * @param {Vue} vm
 */
export function reportError(err, vm) {
  console.error(err);
  if (vm && vm.$options) {
    err.message += `\n\nComponent State:\n\n${JSON.stringify(
      decycle(dumper.getInstanceState(vm))
    )}`;
  }
  if (vm && vm.$store) {
    err.message += `\n\nStore State:\n\n${JSON.stringify(
      decycle(dumper.getStoreState(vm.$store, 'common', 'io'))
    )}`;
  }
  if (err._reported !== true && window.$sentry) {
    window.$sentry.captureException(err);
    err._reported = true;
  }
}

export const error = reportError;

export function isModelDirty(obj) {
  if (Array.isArray(obj)) {
    return obj.some((o) => o.isDirty());
  }
  return obj.isDirty();
}

/**
 * this method will work only for array of scalar not array of object
 * @param {*} a1  array [1,2,4]
 * @param {*} a2  array [1,4,2]
 */
export function isArraySame(a1, a2) {
  if (a1.length !== a2.length) {
    return false;
  }
  if (a1.sort().join(',') === a2.sort().join(',')) {
    return true;
  }
  return false;
}

/**
 * Retry a block of code with a fixed delay and a maximum timeout.
 *
 * @param {Number} delay Milliseconds between executions
 * @param {Number} max Maximum number of milliseconds to keep retrying (wallclock time)
 * @param {Function} fn will be called repeatedly until finish() is called
 *
 */
export function retry(delay, max, fn) {
  const start = performance.now();
  let id;
  // eslint-disable-next-line no-unused-vars
  let count = 0;
  function finish() {
    debug('finished after', count, 'intervals');
    clearInterval(id);
  }
  id = setInterval(() => {
    count++;
    if (performance.now() - start >= max) {
      finish();
      return;
    }
    fn(finish);
  }, delay);
}

export function isValidEmail(emails) {
  if (!Array.isArray(emails)) {
    return emails.match(emailRegex);
  }
  let valid = true;
  emails.forEach((email) => {
    if (!email.match(emailRegex)) {
      valid = false;
    }
  });
  return valid;
}

const bracketedEmailRe = /^<.*?>$/;

/**
 * A very forgiving parser to extract an array of emails from an input string.
 *
 * Emails can be separated by any whitespace or a comma (newlines, tabs,
 * spaces), i.e., /[\s,]+/
 *
 * Emails can be in various formats:
 * - bare email address, e.g., foo@bar.com
 * - name+email, e.g., Foo Bar <foo@bar.com>
 * - mailto, e.g., mailto:foo@bar.com
 *
 * This allows easy copy/paste from a variety of sources and tries to do the right thing.
 * @param {String} str
 */
export function parseValidEmails(str) {
  const emails = str
    .split(/[\s,]+/)
    .map((e) => {
      e = e.trim();
      if (e.match(bracketedEmailRe)) {
        // strip angle brackets to support
        // e.g., <foo@bar.com>
        e = e.substr(1, e.length - 2).trim();
      }
      if (e.startsWith('mailto:')) {
        // strip mailto
        e = e.substr(7);
      }
      return e;
    })
    .filter((e) => !isBlank(e) && e.includes('@'));

  if (!isValidEmail(emails)) {
    return [null, []];
  }

  return [emails.join('\n'), emails];
}

// as it is funny gen IDs using b - https://gist.github.com/jed/982883
export function uuid(a) {
  return a // eslint-disable-next-line no-bitwise
    ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
    : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, uuid);
}

export default {
  isDev,
  createUTCDate,
  inert,
  isArraySame,
  isValidEmail,

  // Returns true if the given value is null or empty string
  isEmptyOrNull(val) {
    return val === null || val === '';
  },

  // Returns true if the given value is null or undefined
  isNil(v) {
    return v === null || v === undefined;
  },

  // Create a numeric string left-padded with zeroes
  zeroPad(num, places) {
    const zero = places - num.toString().length + 1;
    return Array(+(zero > 0 && zero)).join('0') + num;
  },

  startEndDateString(startDate, endDate) {
    const startMonth = moment.utc(startDate).format('MMMM');
    const e = moment.utc(endDate);
    const endMonth = e.format('MMMM');
    let output = startMonth;
    if (startMonth !== endMonth) {
      output += `-${endMonth}`;
    }
    output += ` ${e.year()}`;

    return output;
  },

  isImageType(fileName) {
    const i = fileName.lastIndexOf('.');
    const fileType = fileName.substr(i + 1);
    if (fileType === 'png' || fileType === 'jpg' || fileType === 'jpeg' || fileType === 'gif') {
      return true;
    }
    return false;
  },

  flagOption(statusId, flagOptions) {
    if (_.isEmpty(flagOptions)) {
      return undefined;
    }
    if (_.has(statusToFlagOption, statusId)) {
      return statusToFlagOption[statusId];
    }
    let flagOption;
    switch (statusId) {
      case 7: // Paused
        flagOption = flagOptions.find((color) => color.name === 'Orange');
        break;
      case 2:
      case 20:
      case 11:
      case 16:
        flagOption = flagOptions.find((color) => color.name === 'Yellow');
        break;
      case 4:
      case 22:
      case 18:
        flagOption = flagOptions.find((color) => color.name === 'Purple');
        break;
      case 3:
      case 21:
        flagOption = flagOptions.find((color) => color.name === 'Blue');
        break;
      case 5: // Campaign Live
      case 14:
      case 17:
        flagOption = flagOptions.find((color) => color.name === 'Green');
        break;
      case 9: // ended
      case 13:
        flagOption = flagOptions.find((color) => color.name === 'Teal');
        break;
      case 8: // Disapproved
        flagOption = flagOptions.find((color) => color.name === 'Brown');
        break;
      case 10:
        flagOption = flagOptions.find((color) => color.name === 'Red');
        break;
      default:
        flagOption = undefined;
    }
    statusToFlagOption[statusId] = flagOption;
    return flagOption;
  },

  /**
   * Look up the mapping of status ID to flag option ID
   *
   * @param {Number} statusId
   * @param {Array} flagOptions array of flag options (master data)
   */
  getFlagOptionId(statusId, flagOptions) {
    const flagOption = this.flagOption(statusId, flagOptions);
    return flagOption ? flagOption.id : null;
  },

  elementName(text) {
    if (isBlank(text)) {
      return text;
    }
    const l = text.split(' ').join('_').toLowerCase();
    return `${l}`;
  },

  /**
   * CaseInsensitive sorting on array
   *
   * @param {Array} Array of Objects
   * @param {key} array field used for sorting
   */
  caseInsensitiveSort(array, key) {
    return array.sort((a, b) => {
      const aa = a[key] ? a[key].toLowerCase() : '';
      const bb = b[key] ? b[key].toLowerCase() : '';
      return aa.localeCompare(bb);
    });
  },

  formatNumberAsMillion(num) {
    // Nine Zeroes for Billions
    if (Math.abs(Number(num)) >= 1.0e9) {
      const n = Math.abs(Number(num)) / 1.0e9;
      return `${n.toFixed(2)}B`;
    }
    // Six Zeroes for Millions
    if (Math.abs(Number(num)) >= 1.0e6) {
      const n = Math.abs(Number(num)) / 1.0e6;
      return `${n.toFixed(2)}M`;
    }
    // Three Zeroes for Thousands
    if (Math.abs(Number(num)) >= 1.0e3) {
      const n = Math.abs(Number(num)) / 1.0e3;
      return `${n.toFixed(2)}k`;
    }
    return Math.abs(Number(num));
  },
};
export function formatDate(date, dateFormat = 'yyyy-MM-DD') {
  return date ? moment(new Date(date)).format(dateFormat) : '';
}
